<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { format } from 'date-fns';
import sessionStorage from 'shared/helpers/sessionStorage';

import BillingMeter from './components/BillingMeter.vue';
import BillingCard from './components/BillingCard.vue';
import BillingHeader from './components/BillingHeader.vue';
import DetailItem from './components/DetailItem.vue';
import PurchaseCreditsModal from './components/PurchaseCreditsModal.vue';
import AddonsModal from './components/AddonsModal.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const router = useRouter();
const { currentAccount, isOnChatwootCloud } = useAccount();
// Fork escaleshop: billing por conta também é exposto em self-hosted enterprise (decisão #14).
const { isEnterprise } = useConfig();
const {
  captainEnabled,
  captainLimits,
  documentLimits,
  responseLimits,
  fetchLimits,
  isFetchingLimits,
} = useCaptain();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const BILLING_REFRESH_ATTEMPTED = 'billing_refresh_attempted';

// State for handling refresh attempts and loading
const isWaitingForBilling = ref(false);
const purchaseCreditsModalRef = ref(null);
const addonsModalRef = ref(null);

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

/**
 * Computed property for plan name
 * @returns {string|undefined}
 */
const planName = computed(() => {
  return customAttributes.value.plan_name;
});

// Contas de origem Shopify são cobradas via Shopify Managed Pricing, não Stripe —
// ver Account#shopify_origin? e o plano salvo "shopify-app-store-billing-addon-plan".
const isShopifyOrigin = computed(
  () => currentAccount.value?.origin === 'shopify'
);

const canPurchaseCredits = computed(() => {
  const plan = planName.value?.toLowerCase();
  // Free não compra créditos avulsos (o backend rejeita topup no plano default).
  // Contas Shopify não compram add-ons/créditos via Stripe (só via Managed Pricing).
  return plan && !['hacker', 'free'].includes(plan) && !isShopifyOrigin.value;
});

// Medidor de Contatos Ativos Mensais (CAM) — lê limits.cam exposto pelo endpoint /limits.
const camLimits = computed(() => {
  const cam = currentAccount.value?.limits?.cam;
  return cam ? useCamelCase(cam) : null;
});

const isFreePlan = computed(() => {
  const plan = planName.value?.toLowerCase();
  return !plan || ['free', 'hacker'].includes(plan);
});

// Add-ons só fazem sentido com assinatura paga (Stripe exige subscription ativa);
// contas Shopify não compram add-ons via Stripe (só via Managed Pricing).
const canBuyAddons = computed(
  () => !isFreePlan.value && !isShopifyOrigin.value
);

// Captain está disponível em todos os planos (diferenciado pela cota). No self-hosted
// enterprise o captainEnabled (cloud-gated) é falso, então usamos a presença dos limites.
const showCaptainCard = computed(
  () => captainEnabled.value || (isEnterprise && !!captainLimits.value)
);

/**
 * Computed property for subscribed quantity
 * @returns {number|undefined}
 */
const subscribedQuantity = computed(() => {
  return customAttributes.value.subscribed_quantity;
});

// Limite de canais (caixas de entrada) do plano — exposto pelo endpoint /limits.
// allowed null = ilimitado (Empresarial).
const channelLimit = computed(() => {
  return currentAccount.value?.limits?.inboxes;
});

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  // return date as 12 Jan, 2034
  return format(endDate, 'dd MMM, yyyy');
});

/**
 * Computed property indicating if user has a billing plan
 * @returns {boolean}
 */
const hasABillingPlan = computed(() => {
  return !!planName.value;
});

const fetchAccountDetails = async () => {
  // Contas Shopify não devem ganhar um Stripe customer só por abrir esta tela —
  // o plano delas vem do webhook/consulta de assinatura Shopify (Managed Pricing).
  if (!hasABillingPlan.value && !isShopifyOrigin.value) {
    await store.dispatch('accounts/subscription');
  }
  // Always fetch limits for billing page to show credit usage
  fetchLimits();
};

const handleBillingPageLogic = async () => {
  // Self-hosted community redireciona; cloud e self-hosted enterprise (fork) seguem.
  if (!isOnChatwootCloud.value && !isEnterprise) {
    router.push({ name: 'home' });
    return;
  }

  // Check if we've already attempted a refresh for billing setup
  const billingRefreshAttempted = sessionStorage.get(BILLING_REFRESH_ATTEMPTED);

  // If cloud user, fetch account details first
  await fetchAccountDetails();

  // If still no billing plan after fetch
  if (!hasABillingPlan.value) {
    // If we haven't attempted refresh yet, do it once
    if (!billingRefreshAttempted) {
      isWaitingForBilling.value = true;
      sessionStorage.set(BILLING_REFRESH_ATTEMPTED, true);

      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } else {
      // We've already tried refreshing, so just show the no billing message
      // Clear the flag for future visits
      sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
    }
  } else {
    // Billing plan found, clear any existing refresh flag
    sessionStorage.remove(BILLING_REFRESH_ATTEMPTED);
  }
};

const onClickBillingPortal = () => {
  if (isShopifyOrigin.value) {
    const shop = currentAccount.value?.shopify_shop_domain;
    const appHandle = window.globalConfig?.SHOPIFY_APP_HANDLE;
    if (shop && appHandle) {
      window.location = `https://${shop}/admin/charges/${appHandle}/pricing_plans`;
    } else if (shop) {
      window.location = `https://${shop}/admin/apps`;
    }
    return;
  }
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const openPurchaseCreditsModal = () => {
  purchaseCreditsModalRef.value?.open();
};

const openAddonsModal = () => {
  addonsModalRef.value?.open();
};

const handleAddonSuccess = () => {
  fetchLimits();
};

const handleTopupSuccess = () => {
  // Refresh limits to show updated credit balance
  fetchLimits();
};

onMounted(handleBillingPageLogic);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetchingItem || isWaitingForBilling"
    :loading-message="
      isWaitingForBilling
        ? $t('BILLING_SETTINGS.NO_BILLING_USER')
        : $t('ATTRIBUTES_MGMT.LOADING')
    "
    :no-records-found="!hasABillingPlan && !isWaitingForBilling"
    :no-records-message="$t('BILLING_SETTINGS.NO_BILLING_USER')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_SETTINGS.TITLE')"
        :description="$t('BILLING_SETTINGS.DESCRIPTION')"
        :link-text="$t('BILLING_SETTINGS.VIEW_PRICING')"
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        <BillingCard
          :title="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE')"
          :description="$t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION')"
        >
          <template #action>
            <ButtonV4 sm solid blue @click="onClickBillingPortal">
              {{
                isFreePlan
                  ? $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.UPGRADE')
                  : $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT')
              }}
            </ButtonV4>
          </template>
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="grid lg:grid-cols-4 sm:grid-cols-3 grid-cols-1 gap-2 divide-x divide-n-weak"
          >
            <DetailItem
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')"
              :value="planName"
            />
            <DetailItem
              v-if="subscribedQuantity"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')"
              :value="subscribedQuantity"
            />
            <DetailItem
              v-if="channelLimit"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.CHANNEL_COUNT')"
              :value="
                channelLimit.allowed == null
                  ? $t('BILLING_SETTINGS.CURRENT_PLAN.UNLIMITED')
                  : `${channelLimit.allowed}`
              "
            />
            <DetailItem
              v-if="subscriptionRenewsOn"
              :label="$t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')"
              :value="subscriptionRenewsOn"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="camLimits"
          :title="$t('BILLING_SETTINGS.CAM.TITLE')"
          :description="$t('BILLING_SETTINGS.CAM.DESCRIPTION')"
        >
          <template v-if="canBuyAddons" #action>
            <ButtonV4 sm solid blue @click="openAddonsModal">
              {{ $t('BILLING_SETTINGS.ADDONS.BUY') }}
            </ButtonV4>
          </template>
          <div class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAM.ACTIVE_CONTACTS')"
              :consumed="camLimits.consumed"
              :total-count="camLimits.totalCount"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-if="showCaptainCard"
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.DESCRIPTION')"
        >
          <template #action>
            <div class="flex gap-2">
              <ButtonV4
                sm
                flushed
                slate
                icon="i-lucide-refresh-cw"
                :is-loading="isFetchingLimits"
                @click="fetchLimits"
              >
                {{ $t('BILLING_SETTINGS.CAPTAIN.REFRESH_CREDITS') }}
              </ButtonV4>
              <ButtonV4
                v-if="canPurchaseCredits"
                sm
                solid
                blue
                @click="openPurchaseCreditsModal"
              >
                {{ $t('BILLING_SETTINGS.TOPUP.BUY_CREDITS') }}
              </ButtonV4>
            </div>
          </template>
          <div v-if="captainLimits && responseLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.RESPONSES')"
              v-bind="responseLimits"
            />
          </div>
          <div v-if="captainLimits && documentLimits" class="px-5">
            <BillingMeter
              :title="$t('BILLING_SETTINGS.CAPTAIN.DOCUMENTS')"
              v-bind="documentLimits"
            />
          </div>
        </BillingCard>
        <BillingCard
          v-else
          :title="$t('BILLING_SETTINGS.CAPTAIN.TITLE')"
          :description="$t('BILLING_SETTINGS.CAPTAIN.UPGRADE')"
        >
          <template #action>
            <ButtonV4 sm solid slate @click="onClickBillingPortal">
              {{ $t('CAPTAIN.PAYWALL.UPGRADE_NOW') }}
            </ButtonV4>
          </template>
        </BillingCard>

        <BillingHeader
          class="px-1 mt-5"
          :title="$t('BILLING_SETTINGS.CHAT_WITH_US.TITLE')"
          :description="$t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION')"
        >
          <ButtonV4
            sm
            solid
            slate
            icon="i-lucide-life-buoy"
            @click="onToggleChatWindow"
          >
            {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
          </ButtonV4>
        </BillingHeader>
      </section>
      <PurchaseCreditsModal
        ref="purchaseCreditsModalRef"
        @success="handleTopupSuccess"
      />
      <AddonsModal ref="addonsModalRef" @success="handleAddonSuccess" />
    </template>
  </SettingsLayout>
</template>
